import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  CompaniesService,
  Company,
  CompanyUser,
  User,
  UserCompany,
  UserCompanyRole,
  UserSummary
} from '@eemo/api-client';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { BooleanFormatPipe } from '@/extensions/booleanPipe';
import { EnumHelper } from '@/helpers/enumHelper';
import { UserHelper } from '@/helpers/user.helper';
import { NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { InputComponent } from '../ui-forms/input/input.component';
import { SelectComponent } from '../ui-forms/select/select.component';

@Component({
  selector: 'app-company-user-association',
  templateUrl: './company-user-association.component.html',
  styleUrls: ['./company-user-association.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatIconModule,
    NgFor,
    MatProgressSpinnerModule,
    TranslateModule,
    TooltipDirective,
    InputComponent,
    SelectComponent,
    BooleanFormatPipe
  ]
})
export class CompanyUserAssociationComponent implements OnInit, OnDestroy {
  @Input() company: Company;

  dataSource: MatTableDataSource<UserCompany>;
  displayedColumns = ['userName', 'role', 'isMain', 'actions'];

  getCompanyUsersSub = new Subscription();
  addCompanyUsersSub = new Subscription();
  isLoading = false;
  users: UserSummary[] = [];
  autoCompletedOptions: User[];
  autocompletionExcludeIds = [];

  formSubmitting = false;
  okMess = '';

  roleList: UserCompanyRole[] = EnumHelper.getApiEnumList(UserCompanyRole);

  get form_user() {
    return this.form.get('user') as FormControl<UserSummary>;
  }
  get form_role() {
    return this.form.get('role') as FormControl<UserCompanyRole>;
  }
  get form_main() {
    return this.form.get('main') as FormControl<boolean>;
  }

  form = this.fb.group({
    user: [null, Validators.required],
    role: [UserCompanyRole.MEMBER, Validators.required],
    main: [true, Validators.required]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private cdref: ChangeDetectorRef,
    private companiesService: CompaniesService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getCompanyUsers();
  }

  ngOnDestroy(): void {
    this.getCompanyUsersSub.unsubscribe();
    this.addCompanyUsersSub.unsubscribe();
  }

  confirmDeleteUser(user: CompanyUser) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      // width: '25rem',
      data: {
        message: 'Confirmez-vous la suppression de ' + UserHelper.userSummmaryDisplayName(user.user) + ' ?'
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.removeCompanyUser(user.user.uid);
      }
    });
  }

  onAddUser() {
    if (this.form.valid) {
      this.formSubmitting = true;
      this.addCompanyUser(this.form_user.value.uid, this.form_role.value, this.form_main.value);
    }
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getCompanyUsers() {
    this.isLoading = true;
    this.getCompanyUsersSub = this.companiesService.getCompanyUsers(this.company.uid).subscribe({
      next: (users: CompanyUser[]) => {
        this.isLoading = false;
        const displayedData = [];
        users.forEach((i) => {
          const row = {
            user: i,
            userName: UserHelper.userSummmaryDisplayName(i.user),
            role: this.translate.instant('enum.UserCompanyRole.' + i.role),
            isMain: i.main
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
        this.autocompletionExcludeIds = [...users.map((a: CompanyUser) => a.user.uid)] || [];
      }
    });
  }

  addCompanyUser(userId: string, role: UserCompanyRole, main: boolean) {
    this.formSubmitting = true;
    this.addCompanyUsersSub = this.companiesService.addCompanyUser(this.company.uid, userId, role, main).subscribe({
      next: () => {
        this.okMess = 'Utilisateur ajouté';
        //reset the form
        this.form_user.setValue(null);
        this.form_user.setErrors(null);
        this.form.markAsUntouched();
        this.formSubmitting = false;
        this.getCompanyUsers();
      }
    });
  }

  removeCompanyUser(userId: string) {
    this.isLoading = true;
    this.addCompanyUsersSub = this.companiesService.removeCompanyUser(this.company.uid, userId).subscribe({
      next: () => {
        this.okMess = 'Utilisateur supprimé';
        this.getCompanyUsers();
      }
    });
  }
}
